import type { SourceLanguage } from '@pebblecode/shared';
import {
  AdminUser,
  AdminUserList,
  AssignmentWorkResponse,
  CheckResponse,
  ClassAssignmentsResponse,
  ClassDetail,
  ClassListResponse,
  ClassLiveResponse,
  ConsentInfo,
  CustomExerciseDto,
  CustomExerciseListResponse,
  HomeworkResponse,
  JoinPreview,
  MeResponse,
  OpenAssignmentResponse,
  TeacherAssignment,
  type AdminUserFilter,
  type AdminUserUpdate,
  type CreateAssignmentRequest,
  type CustomExerciseInput,
  type LoginRequest,
  type SignupRequest,
  type UpdateAssignmentRequest,
  ConflictResponse,
  CreateSessionResponse,
  ExerciseDto,
  ExerciseListResponse,
  Equipped,
  HealthResponse,
  LOCAL_DATE_HEADER,
  OWNER_TOKEN_HEADER,
  ProjectDto,
  ProjectListResponse,
  RewardsSummary,
  SessionLookupResponse,
  TutorResponse,
  type CreateProjectRequest,
  type RewardActionRequest,
  type TutorRequest,
  type UiLanguage,
  type UpdateProjectRequest,
} from '@pebblecode/shared';

import { storage } from '../state/store.js';

/** Same-origin in production; proxied to the server by Vite in development. */
const BASE = '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown,
  ) {
    super(message);
  }
}

const TOKEN_KEY = 'pc.ownerToken';

/** The student's calendar date (YYYY-MM-DD in their time zone), for streaks. */
export function localDate(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The anonymous owner token (plan §4.5): 32 random bytes made on first visit and kept in this
 * browser. Whoever holds it can edit the projects it created; the server only stores a hash.
 */
export function ownerToken(): string {
  const existing = storage.get(TOKEN_KEY);
  if (existing && /^[0-9a-f]{64}$/.test(existing)) return existing;
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const token = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  storage.set(TOKEN_KEY, token);
  return token;
}

async function request<T>(
  method: string,
  path: string,
  parse: (data: unknown) => T,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    [OWNER_TOKEN_HEADER]: ownerToken(),
    [LOCAL_DATE_HEADER]: localDate(),
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    // The login session is an HttpOnly cookie (ADR 0014); same-origin requests carry it.
    credentials: 'same-origin',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const msg =
      typeof data === 'object' && data && 'error' in data ? String(data.error) : res.statusText;
    throw new ApiError(res.status, msg, data);
  }
  return parse(data);
}

export const api = {
  health: () => request('GET', '/health', (d) => HealthResponse.parse(d)),
  exercises: (lang: UiLanguage) =>
    request('GET', `/exercises?lang=${lang}`, (d) => ExerciseListResponse.parse(d)),
  exercise: (slug: string, lang: UiLanguage) =>
    request('GET', `/exercises/${encodeURIComponent(slug)}?lang=${lang}`, (d) =>
      ExerciseDto.parse(d),
    ),
  projects: () => request('GET', '/projects', (d) => ProjectListResponse.parse(d).projects),
  project: (id: string) =>
    request('GET', `/projects/${encodeURIComponent(id)}`, (d) => ProjectDto.parse(d)),
  createProject: (body: CreateProjectRequest) =>
    request('POST', '/projects', (d) => ProjectDto.parse(d), body),
  updateProject: (id: string, body: UpdateProjectRequest) =>
    request('PUT', `/projects/${encodeURIComponent(id)}`, (d) => ProjectDto.parse(d), body),
  deleteProject: (id: string) =>
    request('DELETE', `/projects/${encodeURIComponent(id)}`, () => undefined),
  createSession: (projectId: string, classId: string | null = null) =>
    request(
      'POST',
      `/projects/${encodeURIComponent(projectId)}/sessions`,
      (d) => CreateSessionResponse.parse(d),
      { classId },
    ),
  session: (code: string) =>
    request('GET', `/sessions/${encodeURIComponent(code)}`, (d) => SessionLookupResponse.parse(d)),
  closeSession: (code: string) =>
    request('POST', `/sessions/${encodeURIComponent(code)}/close`, () => undefined),
  checkProject: (
    id: string,
    workspace: Record<string, unknown>,
    source: { source: string; sourceLang: SourceLanguage } | null = null,
  ) =>
    request('POST', `/projects/${encodeURIComponent(id)}/check`, (d) => CheckResponse.parse(d), {
      workspace,
      ...(source ?? {}),
    }),
  rewards: () => request('GET', '/rewards', (d) => RewardsSummary.parse(d)),
  rewardAction: (body: RewardActionRequest) =>
    request('POST', '/rewards/action', (d) => RewardsSummary.parse(d), body),
  equip: (patch: Partial<Equipped>) =>
    request('PUT', '/rewards/equip', (d) => Equipped.parse(d), patch),
  explain: (body: TutorRequest) =>
    request('POST', '/ai/explain', (d) => TutorResponse.parse(d), body),
  tutorFeedback: (id: string, helpful: boolean) =>
    request('POST', '/ai/feedback', () => undefined, { id, helpful }),

  // ——— Accounts (ADR 0014/0015) ———
  me: () => request('GET', '/auth/me', (d) => MeResponse.parse(d)),
  signup: (body: SignupRequest) =>
    request('POST', '/auth/signup', (d) => MeResponse.parse(d), body),
  login: (body: LoginRequest) => request('POST', '/auth/login', (d) => MeResponse.parse(d), body),
  logout: () => request('POST', '/auth/logout', () => undefined),
  verifyEmail: (token: string) =>
    request('POST', '/auth/verify', (d) => MeResponse.parse(d), { token }),
  resendEmails: () => request('POST', '/auth/resend', () => undefined),
  forgotPassword: (email: string, uiLang: UiLanguage) =>
    request('POST', '/auth/forgot', () => undefined, { email, uiLang }),
  resetPassword: (token: string, password: string) =>
    request('POST', '/auth/reset', (d) => (d ? MeResponse.parse(d) : null), { token, password }),
  consentInfo: (token: string) =>
    request('GET', `/consent/${encodeURIComponent(token)}`, (d) => ConsentInfo.parse(d)),
  consentDecision: (token: string, decision: 'grant' | 'deny') =>
    request('POST', `/consent/${encodeURIComponent(token)}`, () => undefined, { decision }),
  parentInfo: (token: string) =>
    request('GET', `/parent/${encodeURIComponent(token)}`, (d) => ConsentInfo.parse(d)),
  parentWithdraw: (token: string) =>
    request('DELETE', `/parent/${encodeURIComponent(token)}`, () => undefined),
  updateAccount: (body: { displayName?: string; uiLang?: UiLanguage }) =>
    request('PATCH', '/account', (d) => MeResponse.parse(d), body),
  changePassword: (current: string, next: string) =>
    request('POST', '/account/password', () => undefined, { current, next }),
  requestTeacher: () => request('POST', '/account/teacher-request', (d) => MeResponse.parse(d)),
  deleteAccount: (password: string) => request('DELETE', '/account', () => undefined, { password }),
  adminUsers: (q: string, filter: AdminUserFilter, offset = 0) =>
    request(
      'GET',
      `/admin/users?q=${encodeURIComponent(q)}&filter=${filter}&offset=${offset}`,
      (d) => AdminUserList.parse(d),
    ),
  adminUpdateUser: (id: string, patch: AdminUserUpdate) =>
    request('PATCH', `/admin/users/${encodeURIComponent(id)}`, (d) => AdminUser.parse(d), patch),
  adminDeleteUser: (id: string) =>
    request('DELETE', `/admin/users/${encodeURIComponent(id)}`, () => undefined),
  adminSendReset: (id: string) =>
    request('POST', `/admin/users/${encodeURIComponent(id)}/reset`, () => undefined),

  // ——— Classes and class live sessions (ADR 0016) ———
  classes: () => request('GET', '/classes', (d) => ClassListResponse.parse(d).classes),
  classDetail: (id: string) =>
    request('GET', `/classes/${encodeURIComponent(id)}`, (d) => ClassDetail.parse(d)),
  createClass: (name: string) => request('POST', '/classes', (d) => ClassDetail.parse(d), { name }),
  renameClass: (id: string, name: string) =>
    request('PATCH', `/classes/${encodeURIComponent(id)}`, (d) => ClassDetail.parse(d), { name }),
  deleteClass: (id: string) =>
    request('DELETE', `/classes/${encodeURIComponent(id)}`, () => undefined),
  newClassCode: (id: string) =>
    request('POST', `/classes/${encodeURIComponent(id)}/code`, (d) => ClassDetail.parse(d)),
  removeMember: (classId: string, userId: string) =>
    request(
      'DELETE',
      `/classes/${encodeURIComponent(classId)}/members/${encodeURIComponent(userId)}`,
      () => undefined,
    ),
  joinPreview: (code: string) =>
    request('GET', `/join/${encodeURIComponent(code)}`, (d) => JoinPreview.parse(d)),
  joinClass: (code: string) =>
    request('POST', `/join/${encodeURIComponent(code)}`, (d) => ClassDetail.parse(d)),
  classLive: () => request('GET', '/live', (d) => ClassLiveResponse.parse(d).sessions),

  // ——— Homework and teacher exercises (ADR 0017) ———
  homework: () => request('GET', '/homework', (d) => HomeworkResponse.parse(d).assignments),
  classAssignments: (classId: string) =>
    request('GET', `/classes/${encodeURIComponent(classId)}/assignments`, (d) =>
      ClassAssignmentsResponse.parse(d),
    ),
  createAssignment: (classId: string, body: CreateAssignmentRequest) =>
    request(
      'POST',
      `/classes/${encodeURIComponent(classId)}/assignments`,
      (d) => TeacherAssignment.parse(d),
      body,
    ),
  updateAssignment: (id: string, body: UpdateAssignmentRequest) =>
    request(
      'PATCH',
      `/assignments/${encodeURIComponent(id)}`,
      (d) => TeacherAssignment.parse(d),
      body,
    ),
  deleteAssignment: (id: string) =>
    request('DELETE', `/assignments/${encodeURIComponent(id)}`, () => undefined),
  openAssignment: (id: string) =>
    request('POST', `/assignments/${encodeURIComponent(id)}/open`, (d) =>
      OpenAssignmentResponse.parse(d),
    ),
  submitAssignment: (id: string, submitted: boolean) =>
    request('POST', `/assignments/${encodeURIComponent(id)}/submit`, () => undefined, {
      submitted,
    }),
  assignmentWork: (id: string) =>
    request('GET', `/assignments/${encodeURIComponent(id)}/work`, (d) =>
      AssignmentWorkResponse.parse(d),
    ),
  studentWork: (id: string, userId: string) =>
    request(
      'GET',
      `/assignments/${encodeURIComponent(id)}/work/${encodeURIComponent(userId)}`,
      (d) => ProjectDto.parse(d),
    ),
  giveFeedback: (id: string, userId: string, text: string) =>
    request(
      'PUT',
      `/assignments/${encodeURIComponent(id)}/work/${encodeURIComponent(userId)}/feedback`,
      () => undefined,
      { text },
    ),
  myExercises: () =>
    request('GET', '/my-exercises', (d) => CustomExerciseListResponse.parse(d).exercises),
  myExercise: (slug: string) =>
    request('GET', `/my-exercises/${encodeURIComponent(slug)}`, (d) => CustomExerciseDto.parse(d)),
  createMyExercise: (body: CustomExerciseInput) =>
    request('POST', '/my-exercises', (d) => CustomExerciseDto.parse(d), body),
  updateMyExercise: (slug: string, body: CustomExerciseInput) =>
    request(
      'PUT',
      `/my-exercises/${encodeURIComponent(slug)}`,
      (d) => CustomExerciseDto.parse(d),
      body,
    ),
  deleteMyExercise: (slug: string) =>
    request('DELETE', `/my-exercises/${encodeURIComponent(slug)}`, () => undefined),
};

/** Forget this browser's anonymous identity (after logout, its work belongs to the account). */
export function newOwnerToken(): void {
  storage.remove(TOKEN_KEY);
  ownerToken();
}

/** The current server state carried by a 409, or null for any other error. */
export function conflictOf(err: unknown): ProjectDto | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const parsed = ConflictResponse.safeParse(err.body);
  return parsed.success ? parsed.data.project : null;
}
